import { signal } from '@angular/core';
import { extractPayloadFromToken } from '@iris/modules/auth/utils/extract-payload-from-token';
import { LoginResult } from '@iris/modules/auth/utils/login-result';

const ACCESS_TOKEN_KEY = 'iris_access_token';
const REFRESH_TOKEN_KEY = 'iris_refresh_token';
const ACCESS_TOKEN_EXPIRED_AT_KEY = 'iris_access_token_expired_at';
const ACCESS_TOKEN_EXPIRES_SHIFT = 300;

export class TokenStorage {
  accessTokenKey = `${ACCESS_TOKEN_KEY}_${this.instanceId}`;
  refreshTokenKey = `${REFRESH_TOKEN_KEY}_${this.instanceId}`;
  expiresAtTokenKey = `${ACCESS_TOKEN_EXPIRED_AT_KEY}_${this.instanceId}`;

  constructor(private readonly instanceId: string) { }

  get accessToken(): string {
    return localStorage.getItem(this.accessTokenKey);
  }

  private set accessToken(val: string) {
    localStorage.setItem(this.accessTokenKey, val ?? '');
  }

  readonly accessTokenExpiredAt = signal(+localStorage.getItem(this.expiresAtTokenKey));

  get refreshToken(): string {
    return localStorage.getItem(this.refreshTokenKey);
  }

  private set refreshToken(val: string) {
    localStorage.setItem(this.refreshTokenKey, val ?? '');
  }

  setLoginResult(loginResult: LoginResult): void {
    this.accessToken = loginResult?.accessToken;
    this.setAccessTokenExpiredAt(loginResult?.accessToken);
    this.refreshToken = loginResult?.refreshToken;
  }

  accessTokenIsNotExpired(): boolean {
    if (this.accessToken && !this.accessTokenExpiredAt()) {
      this.setAccessTokenExpiredAt(this.accessToken);
    }
    return !!this.accessToken && !!this.accessTokenExpiredAt() && (Date.now() < this.accessTokenExpiredAt());
  }

  clear(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.expiresAtTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  private extractAccessTokenExpiredAt(accessToken: string): number {
    try {
      if (!accessToken) { return null; }
      const payload = extractPayloadFromToken<{ iat: number; exp: number }>(accessToken);
      const expiresIn = (payload.exp - payload.iat - ACCESS_TOKEN_EXPIRES_SHIFT) * 1000;
      return Date.now() + expiresIn;
    } catch {
      this.clear();
      return null;
    }
  }

  private setAccessTokenExpiredAt(accessToken: string): void {
    const expiresAt = this.extractAccessTokenExpiredAt(accessToken) ?? 0;
    localStorage.setItem(this.expiresAtTokenKey, String(expiresAt));
    this.accessTokenExpiredAt.set(expiresAt);
  }
}
